import React, { useState, useContext } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme, Tooltip, Menu, MenuItem, Avatar, Snackbar, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../Assets/szflogouj.png';
import sznoLogo from '../Assets/populiathyleregis.png';
import LanguageIcon from '@mui/icons-material/Language';
import StateContext from '../Contexts/StateContext';
import DispatchContext from '../Contexts/DispatchContext';
import config, { environment } from '../config';
import Axios from 'axios';

function Header() {
  const { t, i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [openSnack, setOpenSnack] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const GlobalState = useContext(StateContext);
  const GlobalDispatch = useContext(DispatchContext);

  const menuItems = [
    { text: t('Header.Home'), path: `/${i18n.language}/` },
    { text: t('Header.AboutUs'), path: `/${i18n.language}/rolunk` },
    { text: t('Header.SajtoRadar'), path: `/${i18n.language}/sajtoradar` },
    { text: t('Header.Collections'), path: `/${i18n.language}/gyujtemenyek` },
    { text: t('Header.SZGT'), path: `/${i18n.language}/talalkozo` },
    { text: t('Header.Trips'), path: `/${i18n.language}/kirandulasok` },
  ];

  const specialMenuItems = [
    { text: t('Header.Szekelyfold'), path: `/${i18n.language}/feltarasok`, tooltip: t('Header.SzekelyfoldTooltip'), isSpecial: true, icon: <MapIcon sx={{ mr: 1 }} /> },
    { text: t('Header.GSST'), path: `/${i18n.language}/gsst`, tooltip: t('Header.GSSTTooltip'), isSpecial: true, icon: <MapIcon sx={{ mr: 1 }} /> },
  ];

  const handleToggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    if (!window.confirm(t('Header.LogoutConfirm', 'Biztosan ki szeretne jelentkezni?'))) return;

    try {
      if (GlobalState.jwtAccessToken) {
        await Axios.post(
          config[environment].logoutEndpoint,
          {},
          { headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` } }
        );
      }
    } catch (e) {
      console.error("Logout error", e);
    } finally {
      GlobalDispatch({ type: "logout" });
      setOpenSnack(true);
      navigate(`/${i18n.language}/`);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLangMenuClose();
    
    // Update URL with new language
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const languages = ['hu', 'en', 'ro'];
    
    if (languages.includes(pathParts[0])) {
      pathParts[0] = lng;
    } else {
      pathParts.unshift(lng);
    }
    
    navigate(`/${pathParts.join('/')}${window.location.search}${window.location.hash}`);
  };

  const renderNavButtons = (items) => (
    items.map((item) => (
      <Tooltip key={item.text} title={item.tooltip || ''} disableHoverListener={!item.tooltip}>
        <Button
          color="inherit"
          onClick={() => navigate(item.path)}
          sx={{
            mx: 0.5,
            fontWeight: 500,
            fontSize: '0.875rem',
            ...(item.isSpecial && {
              backgroundColor: '#ff0000',
              color: '#fff',
              clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0 100%)',
              px: 3,
              '&:hover': {
                backgroundColor: '#cc0000',
              }
            })
          }}
        >
          {item.icon}
          {item.text}
        </Button>
      </Tooltip>
    ))
  );

  const languageMenu = (
    <Menu
      anchorEl={langAnchorEl}
      open={Boolean(langAnchorEl)}
      onClose={handleLangMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      disableScrollLock
    >
      <MenuItem onClick={() => changeLanguage('hu')}>Magyar</MenuItem>
      <MenuItem onClick={() => changeLanguage('ro')}>Română</MenuItem>
      <MenuItem onClick={() => changeLanguage('en')}>English</MenuItem>
    </Menu>
  );

  const userMenu = (
    <Menu
      anchorEl={userAnchorEl}
      open={Boolean(userAnchorEl)}
      onClose={handleUserMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      disableScrollLock
    >
      <MenuItem onClick={() => { handleUserMenuClose(); navigate(`/${i18n.language}/profile`); }}>{t('Header.Profile')}</MenuItem>
      <MenuItem onClick={handleLogout}>{t('Header.Logout')}</MenuItem>
    </Menu>
  );

  return (
    <AppBar position="sticky" sx={{ backgroundColor: 'primary.main', borderBottom: '4px solid', borderBottomColor: 'accent.goldenYellow' }}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => navigate(`/${i18n.language}/`)}
        >
          <img src={logo} alt="SZFT Logo" style={{ height: '50px', marginRight: '10px' }} />
          {!isMobile && t('Header.Title')}
        </Typography>

        {!isMobile && (
          <Tooltip title="szno.ro">
            <IconButton
              component="a"
              href="https://szno.ro"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mr: 2, p: 0.5 }}
            >
              <img src={sznoLogo} alt="SZNO Logo" style={{ height: '35px' }} />
            </IconButton>
          </Tooltip>
        )}

        {isMobile ? (
          <>
            <IconButton color="inherit" onClick={handleLangMenuOpen} sx={{ ml: 1, mr: 1 }}>
              <LanguageIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                {i18n.language ? i18n.language.substring(0, 2) : 'hu'}
              </Typography>
            </IconButton>
            
            {GlobalState.userIsLogged ? (
              <IconButton color="inherit" onClick={handleUserMenuOpen}>
                 <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '1rem' }}>
                    {GlobalState.userUsername ? GlobalState.userUsername.charAt(0).toUpperCase() : 'U'}
                 </Avatar>
              </IconButton>
            ) : (
              <Button color="inherit" onClick={() => navigate(`/${i18n.language}/login`)}>{t('Header.Login')}</Button>
            )}

            <IconButton color="inherit" onClick={handleToggleDrawer} sx={{ ml: 1 }}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={handleToggleDrawer}>
              <Box sx={{ width: 250 }} role="presentation" onClick={handleToggleDrawer}>
                <List>
                  <ListItem button component="a" href="https://szno.ro" target="_blank" rel="noopener noreferrer">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <img src={sznoLogo} alt="SZNO Logo" style={{ height: '30px', marginRight: '15px' }} />
                      <ListItemText primary="szno.ro" />
                    </Box>
                  </ListItem>
                  <Divider />
                  {[...menuItems, ...specialMenuItems].map((item) => (
                    <ListItem
                      button
                      key={item.text}
                      onClick={() => navigate(item.path)}
                      sx={item.isSpecial ? {
                        backgroundColor: '#ff0000',
                        color: '#fff',
                        mb: 0.5,
                        '&:hover': { backgroundColor: '#cc0000' }
                      } : {}}
                    >
                      {item.isSpecial && <MapIcon sx={{ mr: 2, color: '#fff' }} />}
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={item.isSpecial ? { style: { fontWeight: 'bold' } } : {}}
                      />
                    </ListItem>
                  ))}
                  {GlobalState.userIsLogged && (
                    <ListItem button onClick={() => navigate(`/${i18n.language}/profile`)}>
                      <ListItemText primary={t('Header.Profile')} />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {renderNavButtons(menuItems)}
            
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              {renderNavButtons(specialMenuItems)}
            </Box>

            {GlobalState.userIsLogged ? (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>{GlobalState.userUsername}</Typography>
                <IconButton color="inherit" onClick={handleUserMenuOpen}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '1rem' }}>
                    {GlobalState.userUsername ? GlobalState.userUsername.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                </IconButton>
              </Box>
            ) : (
              <Button variant="outlined" color="inherit" sx={{ ml: 2, borderRadius: '20px' }} onClick={() => navigate(`/${i18n.language}/login`)}>
                {t('Header.Login')}
              </Button>
            )}

            <IconButton color="inherit" onClick={handleLangMenuOpen} sx={{ ml: 1, mr: 2 }}>
              <LanguageIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                {i18n.language ? i18n.language.substring(0, 2) : 'hu'}
              </Typography>
            </IconButton>
          </Box>
        )}
        {languageMenu}
        {userMenu}
        <Snackbar
          open={openSnack}
          message={t('Header.LogoutSuccess', 'Sikeres kijelentkezés')}
          autoHideDuration={2000}
          onClose={() => setOpenSnack(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      </Toolbar>
    </AppBar>
  );
}

export default Header;
